import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import ProtectedApp from './auth/ProtectedApp';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ExportRenderer from './components/ExportRenderer';
import { autoLoadData } from './utils/dataLoader';
import { AlertTriangle } from 'lucide-react';

const OverviewSection = lazy(() => import('./sections/OverviewSection'));
const ProgressSection = lazy(() => import('./sections/ProgressSection'));
const PartnershipSection = lazy(() => import('./sections/PartnershipSection'));
const AdaptiveSection = lazy(() => import('./sections/AdaptiveSection'));
const KeyResultsSection = lazy(() => import('./sections/KeyResultsSection'));

const InnovationSection = lazy(() => import('./sections/InnovationSection'));
const HighlightsSection = lazy(() => import('./sections/HighlightsSection'));
const CustomSection = lazy(() => import('./sections/CustomSection'));

// Portfolio Narrative sections
const PortfolioOverviewSection = lazy(() => import('./sections/portfolio/PortfolioOverviewSection'));
const ImpactAreaSection = lazy(() => import('./sections/portfolio/ImpactAreaSection'));
const RegionalFocusSection = lazy(() => import('./sections/portfolio/RegionalFocusSection'));
const PortfolioPartnershipsSection = lazy(() => import('./sections/portfolio/PortfolioPartnershipsSection'));
const PerformanceManagementSection = lazy(() => import('./sections/portfolio/PerformanceManagementSection'));

function SectionLoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-cgiar-green border-t-transparent rounded-full animate-spin" />
        <p className="text-[var(--text-secondary)]">Loading section...</p>
      </div>
    </div>
  );
}

function AppContent() {
  const { activeSection, setActiveSection, resultsData, isLoading, error, setResultsData, setTocData, setLoading, setError, isExporting, setExportRequest } = useApp();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const autoLoadAttempted = useRef(false);
  const chartCaptureResolver = useRef(null);

  const requestChartCapture = useCallback(() => {
    return new Promise(resolve => {
      chartCaptureResolver.current = resolve;
    });
  }, []);

  const handleCaptureComplete = useCallback((images) => {
    if (chartCaptureResolver.current) {
      chartCaptureResolver.current(images);
      chartCaptureResolver.current = null;
    }
  }, []);

  // Auto-load data from public/data/ on first mount
  useEffect(() => {
    if (!autoLoadAttempted.current && resultsData.length === 0) {
      autoLoadAttempted.current = true;
      autoLoadData({ setResultsData, setTocData, setLoading, setError });
    }
  }, []);

  const renderSection = () => {
    // If no data loaded and not on data section, prompt to load data
    if (resultsData.length === 0 && activeSection !== 'adaptive' && activeSection !== 'keyresults' && activeSection !== 'portfolio-performance') {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-20 h-20 bg-[var(--surface-tertiary)] rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-[var(--text-tertiary)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No Data Loaded</h3>
          <p className="text-[var(--text-secondary)] mb-4 max-w-md">
            Data could not be loaded automatically. Please ensure the data files are placed in the
            <code className="mx-1 px-1.5 py-0.5 bg-[var(--surface-tertiary)] rounded text-sm">public/data/</code> directory and refresh the page.
          </p>
        </div>
      );
    }

    switch (activeSection) {
      case 'overview':
        return <OverviewSection />;
      case 'progress':
        return <ProgressSection />;
      case 'partnerships':
        return <PartnershipSection />;
      case 'adaptive':
        return <AdaptiveSection />;
      case 'keyresults':
        return <KeyResultsSection />;
      case 'innovation':
        return <InnovationSection />;
      case 'highlights':
        return <HighlightsSection />;
      case 'custom':
        return <CustomSection />;
      // Portfolio Narrative sections
      case 'portfolio-overview':
        return <PortfolioOverviewSection />;
      case 'portfolio-impact':
        return <ImpactAreaSection />;
      case 'portfolio-regional':
        return <RegionalFocusSection />;
      case 'portfolio-partnerships':
        return <PortfolioPartnershipsSection />;
      case 'portfolio-performance':
        return <PerformanceManagementSection />;
      default:
        return <OverviewSection />;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-page)]">
      <Header onToggleSidebar={() => setSidebarOpen(!sidebarOpen)} requestChartCapture={requestChartCapture} setExportRequest={setExportRequest} />

      {isExporting && <ExportRenderer onCaptureComplete={handleCaptureComplete} />}

      <div className="flex h-[calc(100vh-60px)]">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <div className="max-w-6xl mx-auto">
            {/* Loading state */}
            {isLoading && (
              <div className="fixed inset-0 bg-[var(--surface-overlay)] z-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-cgiar-green border-t-transparent rounded-full animate-spin" />
                  <p className="text-[var(--text-secondary)]">Processing data...</p>
                </div>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {/* PRMS data disclaimer */}
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">
                <span className="font-semibold">Data Notice:</span> This is a v0 report generated from <span className="font-semibold">data</span> provided by the PRMS technical team as of <span className="font-semibold">26 March 2026</span>. This tool is a supplementary layer built on top of PRMS — it is not the PRMS itself and should not be treated as the authoritative source of truth.
              </p>
            </div>

            {/* Main content */}
            <Suspense fallback={<SectionLoadingFallback />}>
              {renderSection()}
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ProtectedApp>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </ProtectedApp>
  );
}

export default App;
